import type {
  BusinessInquiryFilters,
  InquiryMall,
  InquirySearchType,
  InquiryStatus,
  InquiryType,
} from '../types/businessInquiry';

/** 문의 유형 7종 — 고정 순서(화면설계서 FO-02). 응답 label 이 있으면 그걸 쓰고, 선택지는 이 표를 쓴다. */
export const INQUIRY_TYPES: ReadonlyArray<InquiryType> = [
  'WHOLESALE',
  'GROUP_PURCHASE',
  'ALBUM',
  'MD',
  'CONCERT',
  'POPUP_STORE',
  'VIDEO_PRODUCTION',
];

export const INQUIRY_TYPE_LABELS: Record<InquiryType, string> = {
  WHOLESALE: '도매(사업자)',
  GROUP_PURCHASE: '공동구매(개인)',
  ALBUM: '음반',
  MD: 'MD',
  CONCERT: '공연',
  POPUP_STORE: '팝업스토어',
  VIDEO_PRODUCTION: '영상 제작',
};

export const INQUIRY_STATUSES: ReadonlyArray<InquiryStatus> = ['RECEIVED', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED'];

export const INQUIRY_STATUS_LABELS: Record<InquiryStatus, string> = {
  RECEIVED: '접수',
  IN_PROGRESS: '처리중',
  ON_HOLD: '보류',
  COMPLETED: '완료',
};

export const INQUIRY_STATUS_VARIANT: Record<InquiryStatus, 'info' | 'warn' | 'pending' | 'neutral'> = {
  RECEIVED: 'info',
  IN_PROGRESS: 'warn',
  ON_HOLD: 'pending',
  COMPLETED: 'neutral',
};

/** 목록 접수 몰은 짧게(국문), 상세는 응답 label(국문몰). 설계서 순서: 국문·영문·중문·일문. */
export const INQUIRY_MALLS: ReadonlyArray<InquiryMall> = ['KO', 'EN', 'ZH', 'JA'];

export const INQUIRY_MALL_SHORT: Record<InquiryMall, string> = {
  KO: '국문',
  EN: '영문',
  ZH: '중문',
  JA: '일문',
};

export const INQUIRY_SEARCH_TYPES: ReadonlyArray<InquirySearchType> = ['TITLE', 'COMPANY_NAME', 'NAME', 'EMAIL'];

export const INQUIRY_SEARCH_TYPE_LABELS: Record<InquirySearchType, string> = {
  TITLE: '제목',
  COMPANY_NAME: '회사명',
  NAME: '이름',
  EMAIL: '이메일',
};

export const PAGE_SIZES = [20, 50, 100] as const;

const KST_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' });

/** KST 기준 오늘에서 days 일 전 날짜(YYYY-MM-DD). 0 = 오늘. */
export const kstDateDaysAgo = (days: number, now: Date = new Date()): string =>
  KST_DATE.format(new Date(now.getTime() - days * 24 * 60 * 60 * 1000));

/** 화면 기본값 — 접수일 최근 30일(오늘 포함), 나머지 전체. */
export const defaultInquiryFilters = (now: Date = new Date()): BusinessInquiryFilters => ({
  dateFrom: kstDateDaysAgo(29, now),
  dateTo: kstDateDaysAgo(0, now),
  mallCode: null,
  types: [],
  status: null,
  searchType: null,
  keyword: '',
});

const KST_DATETIME = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

/** UTC ISO → KST "YYYY-MM-DD HH:mm"(withSeconds 면 초까지). 비었거나 읽을 수 없으면 null. */
export const formatKst = (iso: string | null | undefined, withSeconds = false): string | null => {
  if (!iso) return null;
  const d = new Date(iso.replace(/(\.\d{3})\d+/, '$1'));
  if (Number.isNaN(d.getTime())) return null;
  const parts = KST_DATETIME.formatToParts(d);
  const p = (type: Intl.DateTimeFormatPartTypes) => parts.find((x) => x.type === type)?.value ?? '';
  const time = `${p('hour')}:${p('minute')}${withSeconds ? `:${p('second')}` : ''}`;
  return `${p('year')}-${p('month')}-${p('day')} ${time}`;
};

/** 파일 크기 — 1.2MB / 340KB. */
export const formatFileSize = (bytes: number): string => {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)}KB`;
  return `${bytes}B`;
};
